import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ClipLoader } from "react-spinners";
import WindowHeader from "../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../ComboBoxes/SherComboBox";
import ssLogo from "../../assets/images/sslogo.png";
import opnCmp from "../../assets/images/CreateCompanyImages/openCmp.webp";
import exit from "../../assets/images/CreateCompanyImages/Cdbexit.webp";
import "../../styles/OpenCompany.css";

function OpenCompany({ onClose, customerCode, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanyText, setSelectedCompanyText] = useState("");
  const [cmpData, setCmpData] = useState({ Cdata: "" });
  const [highlightedRow, setHighlightedRow] = useState(-1);

  const tableContainerRef = useRef(null);
  const tableRef = useRef(null);
  const selectcmpref = useRef(null);

  const spinnerProps = {
    color: "#026CC3",
    size: 28,
    speedMultiplier: 0.8,
  };

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  }, [onClose, navigate]);

  // Fetch databases / companies for a specific customer code
  const fetchDatabaseNames = useCallback(
    async (codeToFetch) => {
      const code =
        codeToFetch ||
        (Array.isArray(cmpData.Cdata) ? cmpData.Cdata[0] : cmpData.Cdata) ||
        customerCode ||
        localStorage.getItem("customerCode");

      if (!code) return;

      try {
        setIsLoading(true);
        const response = await axios.get(
          `${apiBaseUrl}/main/getalldatabases/${code}`
        );
        const data = Array.isArray(response.data) ? response.data : [];
        setTableData(data);
        if (data.length > 0) {
          setHighlightedRow(0);
        } else {
          setHighlightedRow(-1);
        }
      } catch (error) {
        console.error("Error fetching database names:", error);
        setTableData([]);
        setHighlightedRow(-1);
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl, cmpData.Cdata, customerCode]
  );

  // Fetch available companies for the logged in or passed customer code
  const fetchCmpname = useCallback(async () => {
    const code = customerCode || localStorage.getItem("customerCode");
    if (!code) return;

    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getalldatabases/${code}`
      );
      const cData = Array.isArray(response.data) ? response.data : [];

      // Transform to SherComboBox options shape: { label, value, ... }
      const transformedData = cData.map((item) => ({
        label: item.name || item.CustomerCode || "",
        value: item.CustomerCode || item.Code || "",
        code: item.CustomerCode || item.Code || "",
        name: item.name || "",
        sdate: item.sdate,
        edate: item.edate,
      }));

      setCompanyOptions(transformedData);

      // Auto-select initial company database in the background without pre-filling search text
      if (transformedData.length > 0 && !selectedCompany) {
        const defaultMatch =
          transformedData.find((opt) => opt.value === code) ||
          transformedData[0];
        setSelectedCompany(defaultMatch);
        setSelectedCompanyText("");
        setCmpData({ Cdata: [defaultMatch.value, defaultMatch.label] });
        fetchDatabaseNames(defaultMatch.value);
      }
    } catch (error) {
      console.error("Error fetching company data:", error.message);
    }
  }, [apiBaseUrl, customerCode, selectedCompany, fetchDatabaseNames]);

  // Load initial companies on mount
  useEffect(() => {
    fetchCmpname();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompanySelect = (option) => {
    if (!option) return;
    setSelectedCompany(option);
    setSelectedCompanyText(option.label || "");
    setCmpData({ Cdata: [option.value, option.label] });
    fetchDatabaseNames(option.value);
  };

  // Keep highlighted row smoothly scrolled into view when navigating
  useEffect(() => {
    if (highlightedRow >= 0 && tableContainerRef.current) {
      const rowEl = tableContainerRef.current.querySelector(
        `tr[data-row-index="${highlightedRow}"]`
      );
      if (rowEl) {
        rowEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedRow]);

  const handleCompanyInputChange = (text) => {
    setSelectedCompanyText(text);
    if (text && tableData.length > 0) {
      const lower = text.toLowerCase();
      const matchIndex = tableData.findIndex(
        (row) =>
          (row.name && row.name.toLowerCase().includes(lower)) ||
          (row.Code && row.Code.toLowerCase().includes(lower))
      );
      if (matchIndex >= 0) {
        setHighlightedRow(matchIndex);
      }
    }
  };

  const handleCompanyClear = () => {
    setSelectedCompany(null);
    setSelectedCompanyText("");
    setCmpData({ Cdata: "" });
    setTableData([]);
    setHighlightedRow(-1);
  };

  const handleRowClick = (rowIndex) => {
    setHighlightedRow(rowIndex);
  };

  const onRowSelect = useCallback(
    (rowIndex) => {
      if (rowIndex >= 0 && rowIndex < tableData.length) {
        const selectedRow = tableData[rowIndex];

        if (selectedRow && selectedRow.Code) {
          localStorage.setItem("dbCode", selectedRow.Code);
          sessionStorage.setItem("dbCode", selectedRow.Code);
          if (selectedRow.name) {
            localStorage.setItem("dbName", selectedRow.name);
            sessionStorage.setItem("dbName", selectedRow.name);
          }
        }
        navigate("/login", { state: { selectedRowData: selectedRow } });
      }
    },
    [tableData, navigate]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        if (tableData.length > 0 && highlightedRow >= 0) {
          event.preventDefault();
          onRowSelect(highlightedRow);
        }
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedRow((prev) => Math.min(prev + 1, tableData.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedRow((prev) => Math.max(prev - 1, 0));
      } else if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    },
    [tableData, highlightedRow, onRowSelect, handleClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // The database name is the Code of the currently highlighted row in the table
  const activeDatabaseCode =
    highlightedRow >= 0 && tableData[highlightedRow]
      ? tableData[highlightedRow].Code
      : tableData[0]?.Code ||
        selectedCompany?.value ||
        sessionStorage.getItem("dbCode") ||
        localStorage.getItem("dbCode") ||
        "—";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/65 backdrop-blur-[4px] select-none font-inter"
      style={{ zIndex: zIndex || 9999, fontFamily: "'Inter', sans-serif" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      {/* Modal Dialog Window */}
      <section
        className="w-full max-w-3xl bg-white rounded-xl shadow-2xl border border-blue-900/30 flex flex-col overflow-hidden text-slate-800 font-inter"
        style={{ fontFamily: "'Inter', sans-serif" }}
        data-purpose="open-company-window"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Standard WindowHeader Component */}
        <WindowHeader
          title={
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm tracking-wide text-white">
                Open Company
              </span>
              <span className="text-[10px] text-blue-200/80 font-normal hidden sm:inline">
                SherGold Enterprise Suite v26.4
              </span>
            </div>
          }
          logo={ssLogo}
          onClose={handleClose}
          className="bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white h-10 px-3 shrink-0 rounded-t-xl border-b border-blue-700/60"
          closeBtnClassName="h-7 w-7 my-auto mr-1 bg-rose-600 hover:bg-rose-700 rounded text-white flex items-center justify-center transition-all cursor-pointer active:scale-95"
        />

        {/* Window Toolbar & Filter Bar */}
        <div className="bg-slate-50 px-5 pt-3.5 pb-3 border-b border-slate-200 space-y-3">
          {/* Primary Actions Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              {/* Refined Open Company Button (Primary) */}
              <button
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-blue-600 via-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:scale-95 text-white text-xs font-bold rounded-lg shadow-sm hover:shadow-md border border-blue-700/60 transition-all duration-150 cursor-pointer"
                type="button"
                onClick={() => onRowSelect(highlightedRow)}
                title="Open selected company database (Enter)"
              >
                <img
                  src={opnCmp}
                  alt=""
                  className="w-4 h-4 object-contain drop-shadow-sm brightness-125"
                />
                <span>Open Company</span>
                <span className="text-[10px] bg-blue-800/80 text-blue-100 font-semibold px-1.5 py-0.5 rounded shadow-2xs">
                  Enter
                </span>
              </button>

              {/* Exit Button */}
              <button
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg border border-slate-300 hover:border-slate-400 shadow-sm transition-all cursor-pointer active:scale-95"
                type="button"
                onClick={handleClose}
                title="Exit window (Esc)"
              >
                <img src={exit} alt="" className="w-3.5 h-3.5 object-contain" />
                <span>Exit</span>
                <span className="text-[10px] bg-slate-100 border border-slate-300 text-slate-500 font-semibold px-1 py-0.5 rounded ml-0.5">
                  Esc
                </span>
              </button>
            </div>

            {/* Quick Info Status: Database is the Code in the table */}
            <div className="text-[11px] text-slate-500 font-medium flex items-center gap-1.5 font-inter">
              <span>Database:</span>
              <span className="font-bold text-slate-900 bg-slate-200/80 px-2 py-0.5 rounded border border-slate-300/80 font-inter text-xs">
                {activeDatabaseCode}
              </span>
            </div>
          </div>

          {/* Select Company Search Row using SherComboBox */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold text-slate-700 whitespace-nowrap">
              Select Company:
            </label>
            <div className="relative flex-1">
              <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400 z-10">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </span>
              <SherComboBox
                comboRef={selectcmpref}
                options={companyOptions}
                value={selectedCompanyText}
                placeholder="Type company code or company name..."
                inputPadding="0 28px 0 28px"
                inputHeight="34px"
                inputStyle={{ fontFamily: "'Inter', sans-serif" }}
                optionStyle={{ fontFamily: "'Inter', sans-serif" }}
                inputClassName="text-xs text-slate-900 bg-white font-medium border border-blue-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-600 shadow-sm w-full font-inter"
                className="w-full font-inter"
                dropdownWidth="100%"
                onSelect={handleCompanySelect}
                onInputChange={handleCompanyInputChange}
                onClear={handleCompanyClear}
                onFocus={fetchCmpname}
              />
            </div>
          </div>
        </div>

        {/* Data Grid Table Section */}
        <div className="p-3 bg-slate-100/60">
          <div className="border border-blue-600/70 rounded-lg overflow-hidden bg-white shadow-inner">
            <div
              className="overflow-x-auto max-h-[290px] min-h-[160px] no-scrollbar"
              ref={tableContainerRef}
            >
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-slate-500">
                  <ClipLoader {...spinnerProps} />
                  <span className="text-xs font-medium">
                    Loading companies...
                  </span>
                </div>
              ) : tableData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400 text-xs">
                  <svg
                    className="w-8 h-8 text-slate-300 mb-1.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>No company records found for this database.</span>
                </div>
              ) : (
                <table
                  ref={tableRef}
                  tabIndex="0"
                  className="w-full text-left border-collapse text-xs focus:outline-none"
                >
                  {/* Table Header */}
                  <thead className="sticky top-0 z-10 bg-gradient-to-r from-blue-700 to-sky-700 text-white text-[11px] font-bold tracking-wider uppercase shadow-sm">
                    <tr className="divide-x divide-blue-500/50">
                      <th className="py-2 px-3 text-center w-14" scope="col">
                        SlNo
                      </th>
                      <th className="py-2 px-4 w-36" scope="col">
                        Code
                      </th>
                      <th className="py-2 px-4" scope="col">
                        Company Name
                      </th>
                      <th className="py-2 px-4 w-52" scope="col">
                        Financial Year
                      </th>
                      <th className="py-2 px-3 text-center w-20" scope="col">
                        Action
                      </th>
                    </tr>
                  </thead>
                  {/* Table Body */}
                  <tbody className="divide-y divide-slate-200 font-medium">
                    {tableData.map((rowData, rowIndex) => {
                      const isHighlighted = highlightedRow === rowIndex;
                      return (
                        <tr
                          key={rowIndex}
                          data-row-index={rowIndex}
                          onClick={() => handleRowClick(rowIndex)}
                          onDoubleClick={() => onRowSelect(rowIndex)}
                          className={`transition-colors cursor-pointer border-l-4 ${
                            isHighlighted
                              ? "bg-blue-50 hover:bg-blue-100/70 border-l-blue-600 font-semibold"
                              : rowIndex % 2 === 1
                              ? "bg-slate-50/70 hover:bg-blue-50/50 border-l-transparent"
                              : "bg-white hover:bg-blue-50/50 border-l-transparent"
                          }`}
                        >
                          <td
                            className={`py-2 px-3 text-center ${
                              isHighlighted
                                ? "font-bold text-blue-900"
                                : "text-slate-600"
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              {isHighlighted && (
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse"></span>
                              )}
                              {rowIndex + 1}
                            </div>
                          </td>

                          <td
                            className={`py-2 px-4 uppercase tracking-wide font-bold ${
                              isHighlighted ? "text-blue-950" : "text-slate-900"
                            }`}
                          >
                            {rowData.Code}
                          </td>

                          <td className="py-2 px-4 text-slate-900">
                            <div className="flex items-center gap-1.5">
                              <span
                                className={
                                  isHighlighted
                                    ? "font-bold text-blue-950"
                                    : "font-medium"
                                }
                              >
                                {rowData.name}
                              </span>
                              {isHighlighted && (
                                <span className="text-[9px] bg-blue-100 text-blue-800 border border-blue-200 font-bold px-1.5 py-0.5 rounded shadow-2xs">
                                  Selected
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="py-2 px-4 text-slate-700 font-inter text-[11px]">
                            {rowData.sdate && rowData.edate
                              ? `${rowData.sdate} - ${rowData.edate}`
                              : "- Not Assigned -"}
                          </td>

                          <td className="py-2 px-3 text-center">
                            {isHighlighted ? (
                              <button
                                className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:scale-95 text-white rounded text-[11px] font-bold shadow-xs transition-all cursor-pointer"
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRowSelect(rowIndex);
                                }}
                              >
                                <img
                                  src={opnCmp}
                                  alt=""
                                  className="w-3.5 h-3.5 object-contain brightness-200"
                                />
                                <span>Open</span>
                              </button>
                            ) : (
                              <button
                                className="px-3 py-1 bg-white border border-slate-300 hover:bg-slate-50 hover:border-blue-400 text-slate-700 rounded text-[11px] font-semibold active:scale-95 transition-all cursor-pointer"
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(rowIndex);
                                }}
                              >
                                Select
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Modal Bottom Status & Keyboard Short-cut cues */}
        <div className="bg-slate-50 px-4 py-2 border-t border-slate-200 text-[11px] flex items-center justify-between text-slate-600">
          <div className="flex items-center gap-2">
            <svg
              className="w-3.5 h-3.5 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                clipRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                fillRule="evenodd"
              ></path>
            </svg>
            <span>
              Tip: Double-click row or press{" "}
              <kbd className="px-1 py-0.5 bg-white border border-slate-300 rounded font-semibold text-slate-800 shadow-2xs">
                Enter
              </kbd>{" "}
              to open.
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="font-medium">
              Total:{" "}
              <strong className="text-slate-900">{tableData.length}</strong>{" "}
              companies
            </span>
            <span className="text-slate-300">|</span>
            <div className="flex items-center gap-1.5 text-slate-500 font-inter text-[10px]">
              <span className="bg-slate-200/80 px-1 py-0.2 rounded text-slate-700 font-bold">
                ↑↓
              </span>{" "}
              Navigate
              <span className="bg-slate-200/80 px-1 py-0.2 rounded text-slate-700 font-bold ml-1">
                Enter
              </span>{" "}
              Open
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default OpenCompany;
